import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { CircleHelp, X, MessageCircle, BookOpen, Target, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { useObjectives } from "../../objectives/hooks/useObjectives";

// Sub-components
import HeroMetrics from "../components/HeroMetrics";
import EfficiencyChart from "../components/EfficiencyChart";
import HealthStatus from "../components/HealthStatus";

const MOCK_BRAND_ID = "brand-demo";

const DashboardPage = () => {
    const [showTutorial, setShowTutorial] = useState(false);

    // Trae los objetivos reales del backend para mostrar el count en vivo
    const { objectives, loading: objectivesLoading } = useObjectives(MOCK_BRAND_ID);

    const activeObjectives = useMemo(
        () => objectives.filter((o) => o.status === "active"),
        [objectives],
    );

    // KPIs reales derivados de las metrics acumuladas de los objetivos activos
    const aggregateMetrics = useMemo(() => {
        const totals = activeObjectives.reduce(
            (acc, o) => {
                acc.peopleReached += o.metrics.peopleReached || 0;
                acc.conversions += o.metrics.conversions || 0;
                acc.revenue += o.metrics.revenueGeneratedUSD || 0;
                acc.runsCount += o.metrics.runsCount || 0;
                return acc;
            },
            { peopleReached: 0, conversions: 0, revenue: 0, runsCount: 0 },
        );

        // ROI estimado: ingresos / costo total de envíos
        // Tarifa promedio WhatsApp Business: ~USD $1.50 por conversación
        // (esto da ROIs creíbles 200-800%, no 18000% como antes)
        const estimatedSpend = totals.peopleReached * 1.5;
        const roi = estimatedSpend > 0 ? (totals.revenue / estimatedSpend) * 100 : 0;

        return {
            roiPercent: Math.round(roi),
            revenueUSD: totals.revenue,
            peopleReached: totals.peopleReached,
            conversions: totals.conversions,
            runsCount: totals.runsCount,
        };
    }, [activeObjectives]);

    // Audit DSH-01/02: EfficiencyChart y HealthStatus muestran datos
    // hardcodeados. Sólo los mostramos cuando hay actividad real, para no
    // contradecir el hero vacío ("Sin datos todavía") ni el onboarding.
    const hasRealData = aggregateMetrics.runsCount > 0;

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-6 relative">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-4">
                <div>
                    <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">
                        Dashboard
                    </h1>
                    <p className="text-slate-500 text-lg">
                        Tu resumen de desempeño de campañas activas.
                    </p>
                </div>
                <div className="flex gap-4">
                    <Button
                        variant={showTutorial ? "primary" : "secondary"}
                        className="!py-2 !px-4"
                        onClick={() => setShowTutorial(!showTutorial)}
                    >
                        {showTutorial ? <X size={16} /> : <CircleHelp size={16} />}
                        {showTutorial ? "Cerrar tutorial" : "Tutorial"}
                    </Button>
                </div>
            </div>

            {/* ── Onboarding checklist — solo cuando no hay campañas activas ── */}
            {!objectivesLoading && activeObjectives.length === 0 && (
                <div className="bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 rounded-2xl border border-violet-100 p-6 animate-fade-in">
                    <div className="flex items-start gap-4 mb-5">
                        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Sparkles size={18} className="text-violet-600" />
                        </div>
                        <div>
                            <h2 className="font-bold text-slate-900 text-base">Primeros pasos con Deenex AI</h2>
                            <p className="text-sm text-slate-500 mt-0.5">Completá estos 3 pasos para empezar a ver resultados reales acá.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <OnboardingStep
                            num={1}
                            icon={MessageCircle}
                            title="Conectá WhatsApp"
                            desc="Vinculá tu número de negocio para empezar a enviar mensajes."
                            to="/whatsapp/accounts"
                        />
                        <OnboardingStep
                            num={2}
                            icon={BookOpen}
                            title="Configurá el Manual de tu IA"
                            desc="Definí el tono, las palabras prohibidas y el horario de envío."
                            to="/brand"
                        />
                        <OnboardingStep
                            num={3}
                            icon={Target}
                            title="Activá tu primera campaña"
                            desc="Elegí un preset (cumpleaños, carrito, reactivación) y lanzala."
                            to="/objectives/new"
                            cta="Crear campaña"
                            highlight
                        />
                    </div>
                </div>
            )}

            {/* Hero Metrics — ahora con datos reales agregados */}
            <HeroMetrics
                activeObjectives={activeObjectives}
                aggregateMetrics={aggregateMetrics}
                loading={objectivesLoading}
                showTutorial={showTutorial}
            />

            {/* Chart + Salud — sólo con datos reales (Audit DSH-01/02) */}
            {hasRealData && (
                <>
                    <EfficiencyChart showTutorial={showTutorial} />
                    <HealthStatus showTutorial={showTutorial} />
                </>
            )}
            </div>
        </div>
    );
};

/* ─── Sub-component: OnboardingStep ─── */
function OnboardingStep({
    num,
    icon: Icon,
    title,
    desc,
    to,
    cta,
    highlight,
}: {
    num: number;
    icon: typeof Target;
    title: string;
    desc: string;
    to: string;
    cta?: string;
    highlight?: boolean;
}) {
    return (
        <Link
            to={to}
            className={`group flex flex-col gap-3 p-4 rounded-xl border transition-all ${
                highlight
                    ? "bg-violet-600 border-violet-500 text-white hover:bg-violet-700"
                    : "bg-white border-slate-200 hover:border-violet-300 hover:shadow-sm"
            }`}
        >
            <div className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${highlight ? "bg-white/20 text-white" : "bg-violet-100 text-violet-700"}`}>
                    {num}
                </span>
                <Icon size={15} className={highlight ? "text-white/80" : "text-slate-500"} />
                <span className={`text-sm font-semibold ${highlight ? "text-white" : "text-slate-800"}`}>{title}</span>
            </div>
            <p className={`text-xs leading-relaxed ${highlight ? "text-white/80" : "text-slate-500"}`}>{desc}</p>
            <div className={`flex items-center gap-1 text-xs font-semibold mt-auto ${highlight ? "text-white" : "text-violet-600"}`}>
                {cta || "Ir ahora"}
                <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </div>
        </Link>
    );
}

export default DashboardPage;
